import { LitElement, html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class RdSideBar extends LitElement {
  // Disable shadow DOM - use light DOM instead
  createRenderRoot() {
    return this;
  }

  static properties = {
    //menuType =  {name:'', icon:'', isSelected, hoverable, selectable, disabled, position (top, bottom, middle), defaultTab, hidden, tooltip, notification }
    // selectable: false = menu can be clicked but won't be selected (useful for triggering actions)
    // defaultTab: name of the tab to select when this menu is clicked (if not specified, first tab of menu will be selected)
    // hidden: true = menu will not be rendered
    // tooltip: string = tooltip text shown on hover
    // notification: {type: 'info'|'warning'|'error'|'success'|'custom', count: number, color: string} = shows colored dot with optional count
    menus: { type: Array },
    menuSelected: { type: String },
    onMenuSelected: { type: Function },
    onMenuSelecting: { type: Function }, //cancellable
    collapseOnMenuClick: { type: Boolean },

    //tabType =  {name:'', icon:'', isSelected, disabled, hidden, isChanged, tooltip, closeable, searchable, searchPlaceholder, actions, notification }
    // hidden: true = tab will not be rendered
    // isChanged: true = shows dot indicator (unsaved changes)
    // tooltip: string = tooltip text shown on hover
    // closeable: true = shows X button to close tab
    // searchable: true = shows search box for this tab's content
    // searchPlaceholder: string = placeholder text for search box
    // actions: [{icon, label, tooltip, onClick(action, tab, event)}] = custom action buttons (you handle dropdown logic in onClick)
    // notification: {type: 'info'|'warning'|'error'|'success'|'custom', count: number, color: string} = shows colored dot with optional count
    tabs: { type: Array },
    selectedTab: { type: String },
    onTabSelected: { type: Function },
    onTabSelecting: { type: Function }, //cancellable
    onTabClose: { type: Function }, // called when tab close button clicked, return false to prevent close
    logo: { type: String, reflect: true },
    onLogoClicked: { type: Function },

    // Loader state
    loading: { type: Boolean },
    loadingMessage: { type: String },

    // Feature toggles
    groupsCollapsible: { type: Boolean }, // allow menu groups to be collapsed
    keyboardNavigationEnabled: { type: Boolean }, // enable keyboard navigation
    splitMode: { type: String } // 'none' | 'horizontal' | 'vertical'
  };

  constructor() {
    super();
    this.menus = [];
    this.menuSelected = '';
    this.onMenuSelected = (menuName) => {
    };
    this.onMenuSelecting = (menuName) => true;
    this.collapseOnMenuClick = false;
    this.tabs = [];
    this._selectedTab = ''; // Internal property for selectedTab
    this.onTabSelected = (tabName) => {
    };
    this.onTabSelecting = (tabName) => true;
    this.onTabClose = (tabName) => true; // return false to prevent close
    this.logo = '';
    this.onLogoClicked = () => {
    };
    this.loading = false;
    this.loadingMessage = 'Loading...';

    // Feature toggles
    this.groupsCollapsible = false;
    this.keyboardNavigationEnabled = false;
    this.splitMode = 'none'; // 'none' | 'horizontal' | 'vertical'

    // Track content containers by tab name
    this._contentContainers = new Map();

    // Track collapsed group states
    this._collapsedGroups = new Set();

    // Track search visibility by tab name
    this._searchVisible = new Map();

    // Track search values by tab name
    this._searchValues = new Map();

    // Track focused element index for keyboard navigation
    this._focusedMenuIndex = -1;
    this._focusedTabIndex = -1;

    // Track collapsed category/group states
    this._collapsedCategories = new Map(); // Map<tabName, Set<categoryId>>
    this._collapsedCategoryGroups = new Map(); // Map<tabName, Map<categoryId, Set<groupId>>>

    // Intelligent element management
    this._elementRegistry = new Map(); // Map<elementId, {element, metadata, rendered}>
    this._categoryRegistry = new Map(); // Map<tabName, Map<categoryId, {name, icon, collapsed}>>
    this._groupRegistry = new Map(); // Map<categoryId, Map<groupId, {name, icon, collapsed}>>
    this._elementsByCategory = new Map(); // Map<categoryId, Array<elementId>>
    this._elementsByGroup = new Map(); // Map<groupId, Array<elementId>>
    this._pendingElements = new Map(); // Map<tabName, Array<element>> - elements waiting to be rendered
    this._observedElements = new WeakMap(); // Track visibility observers

    // ── Tree Navigation API state ────────────────────────────────────────────
    this._treeTabs    = new Map(); // Map<tabId, TabTreeDef>
    this._treeGroups  = new Map(); // Map<groupId, GroupTreeNode>
    this._treeTabRoots = new Map(); // Map<tabId, string[]>  – root-level group IDs
    this._treeExpanded = new Set(); // Set<groupId>
    this._treeItems   = new Map(); // Map<groupId, ItemNode[]>
    this._treeContent = new Map(); // Map<groupId, HTMLElement>
  }

  get collapsed() {
    this.menuSelected === '';
  }

  set collapsed(val) {
    if (this.menuSelected === '' && val === true) return;
    if (val === true) {
      //store selected menu and tab
      this._storedMenuSelected = this.menuSelected;
      this._storedTabSelected = this.selectedTab;
      this.menuSelected = '';
    } else {
      //restore selected menu and tab
      this.menuSelected = this._storedMenuSelected || '';
      this.selectedTab = this._storedTabSelected || '';
    }
  }

  // Override selectedTab setter to automatically set menuSelected
  set selectedTab(value) {
    const oldValue = this._selectedTab;
    this._selectedTab = value;

    // Auto-select the menu that contains this tab
    if (value && this.tabs && Array.isArray(this.tabs)) {
      const tab = this.tabs.find((t) => t.name === value);
      if (tab && tab.menuName) {
        // Tab exists, set its menu
        if (this.menuSelected !== tab.menuName) {
          this.menuSelected = tab.menuName;
        }
      } else {
        // Tab does not exist, clear selection
        this._selectedTab = '';
        this.menuSelected = '';
      }
    } else if (value) {
      // Value is set but tabs array is not ready or tab doesn't exist
      // Clear selection
      this._selectedTab = '';
      this.menuSelected = '';
    }

    this.requestUpdate('selectedTab', oldValue);
  }

  get selectedTab() {
    return this._selectedTab;
  }

  connectedCallback() {
    super.connectedCallback();

    // Setup keyboard navigation if enabled
    if (this.keyboardNavigationEnabled) {
      this._setupKeyboardNavigation();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up keyboard navigation
    if (this._keyboardHandler) {
      this.removeEventListener('keydown', this._keyboardHandler);
    }
  }

  _setupKeyboardNavigation() {
    this._keyboardHandler = (e) => this._handleKeyDown(e);
    this.addEventListener('keydown', this._keyboardHandler);

    // Make sidebar focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  _handleKeyDown(e) {
    if (!this.keyboardNavigationEnabled) return;

    const visibleMenus = this._getMenusByPosition('top')
      .concat(this._getMenusByPosition('middle'), this._getMenusByPosition('bottom'))
      .filter((m) => !m.hidden);

    const currentTabs = this.tabs.filter((t) => t.menuName === this.menuSelected && !t.hidden);

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        if (this._focusedMenuIndex > 0) {
          this._focusedMenuIndex--;
          this._updateMenuFocus(visibleMenus);
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (this._focusedMenuIndex < visibleMenus.length - 1) {
          this._focusedMenuIndex++;
          this._updateMenuFocus(visibleMenus);
        }
        break;

      case 'ArrowLeft':
        e.preventDefault();
        if (this.menuSelected && this._focusedTabIndex > 0) {
          this._focusedTabIndex--;
          this._updateTabFocus(currentTabs);
        }
        break;

      case 'ArrowRight':
        e.preventDefault();
        if (this.menuSelected && this._focusedTabIndex < currentTabs.length - 1) {
          this._focusedTabIndex++;
          this._updateTabFocus(currentTabs);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (this._focusedMenuIndex >= 0 && this._focusedMenuIndex < visibleMenus.length) {
          this._handleMenuClick(visibleMenus[this._focusedMenuIndex]);
        } else if (this._focusedTabIndex >= 0 && this._focusedTabIndex < currentTabs.length) {
          this._handleTabClick(currentTabs[this._focusedTabIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        this.menuSelected = '';
        this._focusedTabIndex = -1;
        this.requestUpdate();
        break;

      case 'Tab':
        // Let default tab behavior work
        break;
    }
  }

  _updateMenuFocus(visibleMenus) {
    // Visual feedback handled by CSS :focus-within
    this.requestUpdate();
  }

  _updateTabFocus(currentTabs) {
    // Visual feedback handled by CSS :focus-within
    this.requestUpdate();
  }

  _createIconElement(iconClass, uniqueId) {
    // Create icon in light DOM
    const icon = document.createElement('i');
    icon.className = iconClass;
    icon.setAttribute('data-icon-id', uniqueId);
    return icon;
  }

  _handleMenuClick(menu) {
    if (menu.disabled) return;
    if (CanvasManager.isInPreview) return;
    // Call cancellable callback
    const shouldProceed = this.onMenuSelecting(menu.name);
    if (shouldProceed === false) return;

    // Always trigger the callback

    // Only update selection if selectable is not explicitly false
    if (menu.selectable !== false) {
      if (!this.collapseOnMenuClick && !menu.collapse) {
        if (this.menuSelected === menu.name) {
          //this.menuSelected = '';
          console.log('Menu clicked again, but collapseOnMenuClick is false, so not collapsing. Name:', menu.name);
          return;
        }
      }
      // Toggle menu selection (close if same menu clicked)
      const newSelection = this.menuSelected === menu.name ? '' : menu.name;
      this.menuSelected = newSelection;
      this._storedMenuSelected = '';
      this._storedTabSelected = '';
      // If menu is being opened, select the default tab or first tab
      if (newSelection) {
        this.onMenuSelected(menu.name);
        this._selectDefaultTabForMenu(menu);
      }

      this._updateContentVisibility();
    }

    this.requestUpdate();
  }

  /**
   * Select the default tab for a menu, or the first available tab
   * @param {Object} menu - The menu object
   * @private
   */
  _selectDefaultTabForMenu(menu) {
    // Get all tabs for this menu
    const menuTabs = this.tabs.filter((tab) => tab.menuName === menu.name);

    if (menuTabs.length === 0) return;

    // Check if menu has a defaultTab defined
    if (menu.defaultTab) {
      const defaultTab = menuTabs.find((tab) => tab.name === menu.defaultTab);
      if (defaultTab && !defaultTab.disabled) {
        this.selectedTab = defaultTab.name;
        this.onTabSelected(defaultTab.name);
        return;
      }
    }

    // Otherwise, select the first non-disabled tab
    const firstTab = menuTabs.find((tab) => !tab.disabled);
    if (firstTab) {
      this.selectedTab = firstTab.name;
      this.onTabSelected(firstTab.name);
    }
  }

  _handleTabClick(tab) {
    if (tab.disabled) return;

    // Call cancellable callback
    const shouldProceed = this.onTabSelecting(tab.name);
    if (shouldProceed === false) return;

    const prevTab = this.selectedTab;
    this.selectedTab = tab.name;
    this.onTabSelected(tab.name);
    this._updateContentVisibility();
    this.requestUpdate();

    // ── Tree API events ──────────────────────────────────────────────────────
    if (prevTab && this._treeTabs?.has(prevTab)) {
      const def = this._treeTabs.get(prevTab);
      def.onDeselect?.(def);
      this.dispatchEvent(new CustomEvent('rd-tab-deselect', {
        bubbles: true, composed: true,
        detail: { tabId: prevTab, tab: def },
      }));
    }
    if (this._treeTabs?.has(tab.name)) {
      const def = this._treeTabs.get(tab.name);
      def.onSelect?.(def);
      this.dispatchEvent(new CustomEvent('rd-tab-select', {
        bubbles: true, composed: true,
        detail: { tabId: tab.name, tab: def },
      }));
    }
  }

  _handleLogoClick() {
    this.onLogoClicked();
  }

  /**
   * Set or update content for a specific tab
   * @param {string} tabName - Name of the tab
   * @param {HTMLElement|string} content - DOM element or HTML string
   */
  setTabContent(tabName, content, append = false) {
    // Find or create content area inside the tabs-panel
    const tabsPanel = this.querySelector('.tabs-panel');
    if (!tabsPanel) {
      console.error('Tabs panel not found');
      return;
    }

    let contentArea = tabsPanel.querySelector('.rd-sidebar-content-area');
    if (!contentArea) {
      contentArea = document.createElement('div');
      contentArea.className = 'rd-sidebar-content-area';
      tabsPanel.appendChild(contentArea);
    }

    // Create new content container
    let container = this._contentContainers.get(tabName);
    if (container && !append) {
      // Clear existing content
      container.innerHTML = '';
    }
    if (!container) {
      container = document.createElement('div');
      container.className = 'rd-tab-content-container';
      container.setAttribute('data-tab', tabName);
      container.style.display = 'none'; // Hidden by default
      this._contentContainers.set(tabName, container);

      // Append to content area
      contentArea.appendChild(container);
    }
    // Add content
    if (content instanceof HTMLElement) {
      // If it's a DOM element, append it
      container.appendChild(content);
    } else if (typeof content === 'string') {
      // If it's a string, set as innerHTML
      container.innerHTML = content;
    } else if (content?.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      // If it's a document fragment
      container.appendChild(content);
    }

    // Store reference

    // Update visibility if this is the active tab
    this._updateContentVisibility();
  }

  appendTabContent(tabName, content) {
    this.setTabContent(tabName, content, true);
  }

  /**
   * Add an element to the sidebar with automatic category/group creation
   * Supports smart element metadata and lifecycle events
   *
   * @param {HTMLElement|string} element - The element to add (with rd metadata)
   * @param {Object} options - Override options
   *
   * Element metadata (as properties or attributes):
   * - element.menu / data-menu: Target menu name
   * - element.tab / data-tab: Target tab name
   * - element.category / data-category: Category ID
   * - element.categoryName / data-category-name: Category display name
   * - element.categoryIcon / data-category-icon: Category icon class
   * - element.group / data-group: Group ID
   * - element.groupName / data-group-name: Group display name
   * - element.groupIcon / data-group-icon: Group icon class
   * - element.atIndex / data-at-index: Specific index position
   * - element.atAfter / data-at-after: Place after element with this ID
   * - element.atBefore / data-at-before: Place before element with this ID
   * - element.id: Element ID (required)
   * - element.name: Element name/label
   *
   * Lifecycle events (element.rd):
   * - rd.initialized(): Called when element is added to DOM
   * - rd.onCreated(): Called when element becomes visible for the first time
   * - rd.visibilityChanged(visible): Called when visibility changes
   */
  addElement(element, options = {}) {
    // Parse element metadata
    const metadata = this._parseElementMetadata(element, options);

    if (!metadata.id) {
      console.error('Element must have an ID (element.id or data-id attribute)');
      return null;
    }

    if (!metadata.tab) {
      console.error('Element must specify a tab (element.tab or data-tab attribute)');
      return null;
    }
    let oldElement = this._elementRegistry.get(metadata.id);
    if (oldElement) {
      oldElement.element?.remove();
    }
    // Store element in registry
    this._elementRegistry.set(metadata.id, {
      element: element,
      metadata: metadata,
      rendered: false,
      visible: false,
      created: false
    });

    // Auto-create category if needed
    if (metadata.category) {
      this._ensureCategory(metadata.tab, metadata.category, {
        name: metadata.categoryName || metadata.categoryRaw,
        rawName: metadata.categoryRaw,
        icon: metadata.categoryIcon,
        collapsed: metadata.categoryCollapsed || false
      });
    }

    // Auto-create group if needed
    if (metadata.group && metadata.category) {
      this._ensureGroup(metadata.tab, metadata.category, metadata.group, {
        name: metadata.groupName || metadata.groupRaw,
        rawName: metadata.groupRaw,
        icon: metadata.groupIcon,
        collapsed: metadata.groupCollapsed || false
      });
    }

    // Track element in category/group
    if (metadata.category) {
      if (!this._elementsByCategory.has(metadata.category)) {
        this._elementsByCategory.set(metadata.category, []);
      }
      this._elementsByCategory.get(metadata.category).push(metadata.id);
    }

    if (metadata.group) {
      if (!this._elementsByGroup.has(metadata.group)) {
        this._elementsByGroup.set(metadata.group, []);
      }
      this._elementsByGroup.get(metadata.group).push(metadata.id);
    }

    // Add to pending elements if tab not yet selected
    if (this.selectedTab !== metadata.tab) {
      if (!this._pendingElements.has(metadata.tab)) {
        this._pendingElements.set(metadata.tab, []);
      }
      this._pendingElements.get(metadata.tab).push(metadata.id);
    } else {
      // Render immediately if tab is active
      // First ensure category/group exist in DOM
      if (metadata.category) {
        const catConfig = this._categoryRegistry.get(metadata.tab)?.get(metadata.category);
        if (catConfig) {
          const existingCat = this.getCategory(metadata.tab, metadata.category);
          if (!existingCat) {
            this.addCategory(metadata.tab, catConfig);
          }
        }
      }

      if (metadata.group && metadata.category) {
        const groupConfig = this._groupRegistry.get(metadata.category)?.get(metadata.group);
        if (groupConfig) {
          const existingGroup = this.getGroup(metadata.tab, metadata.category, metadata.group);
          if (!existingGroup) {
            this.addGroupToCategory(metadata.tab, metadata.category, groupConfig);
          }
        }
      }

      // Now render the element
      this._renderElement(metadata.id);
    }

    return metadata.id;
  }

  /**
   * Update change indicators based on element.modified state
   * Called when an element dispatches 'update-sidebar' event
   * @private
   */
  _updateFromElement() {
    // Clear all change states first
    const changeStates = {
      tabs: new Map(), // Map<tabName, boolean>
      categories: new Map(), // Map<categoryId, boolean>
      groups: new Map() // Map<groupId, boolean>
    };

    // Scan all elements for modified state
    this._elementRegistry.forEach((entry, elementId) => {
      const { domElement, metadata } = entry;

      // Check if element has modified property
      const isModified = domElement?.modified === true;

      if (isModified) {
        // Mark tab as changed
        // if (metadata.tab) {
        //   changeStates.tabs.set(metadata.tab, true);
        // }

        // Mark category as changed
        if (metadata.category) {
          changeStates.categories.set(metadata.category, true);
        }

        // Mark group as changed
        if (metadata.group) {
          changeStates.groups.set(metadata.group, true);
        }
      }
    });

    // Update tab isChanged properties
    // this.tabs.forEach((tab) => {
    //   tab.isChanged = changeStates.tabs.get(tab.name) === true;
    // });

    // Update category change dots
    changeStates.categories.forEach((hasChanges, categoryId) => {
      this._updateCategoryChangeDot(categoryId, hasChanges);
    });

    // Remove dots from categories not in changeStates
    this._categoryRegistry.forEach((categories, tabName) => {
      categories.forEach((category, categoryId) => {
        if (!changeStates.categories.has(categoryId)) {
          this._updateCategoryChangeDot(categoryId, false);
        }
      });
    });

    // Update group change dots
    changeStates.groups.forEach((hasChanges, groupId) => {
      this._updateGroupChangeDot(groupId, hasChanges);
    });

    // Remove dots from groups not in changeStates
    this._groupRegistry.forEach((groups, categoryId) => {
      groups.forEach((group, groupId) => {
        if (!changeStates.groups.has(groupId)) {
          this._updateGroupChangeDot(groupId, false);
        }
      });
    });

    // Trigger re-render to update menu and tab dots
    this.requestUpdate();
  }

  /**
   * Update change dot for a category
   * @private
   */
  _updateCategoryChangeDot(categoryId, hasChanges) {
    // Find category element in DOM
    const categoryEl = this.querySelector(`[data-category-id="${categoryId}"]`);
    if (!categoryEl) return;

    const header = categoryEl.querySelector('.rd-category-header');
    if (!header) return;

    // Remove existing dot
    const existingDot = header.querySelector('.change-dot');
    if (existingDot) {
      existingDot.remove();
    }

    // Add new dot if there are changes
    if (hasChanges) {
      const dot = document.createElement('span');
      dot.className = 'change-dot';
      dot.title = 'Modified';
      // Insert before chevron if exists, otherwise append
      const chevron = header.querySelector('.rd-category-chevron');
      if (chevron) {
        header.insertBefore(dot, chevron);
      } else {
        header.appendChild(dot);
      }
    }
  }

  /**
   * Update change dot for a group
   * @private
   */
  _updateGroupChangeDot(groupId, hasChanges) {
    // Find group element in DOM
    const groupEl = this.querySelector(`[data-group-id="${groupId}"]`);
    if (!groupEl) return;

    const header = groupEl.querySelector('.rd-group-header');
    if (!header) return;

    // Remove existing dot
    const existingDot = header.querySelector('.change-dot');
    if (existingDot) {
      existingDot.remove();
    }

    // Add new dot if there are changes
    if (hasChanges) {
      const dot = document.createElement('span');
      dot.className = 'change-dot';
      dot.title = 'Modified';
      // Insert before chevron if exists, otherwise append
      const chevron = header.querySelector('.rd-group-chevron');
      if (chevron) {
        header.insertBefore(dot, chevron);
      } else {
        header.appendChild(dot);
      }
    }
  }

  /**
   * Generate unique category ID scoped to tab
   * @private
   */
  _generateCategoryId(tabName, categoryId) {
    return `${tabName}__${categoryId}`;
  }

  /**
   * Generate unique group ID scoped to tab and category
   * @private
   */
  _generateGroupId(tabName, categoryId, groupId) {
    return `${tabName}__${categoryId}__${groupId}`;
  }

  /**
   * Parse element metadata from properties or attributes
   * @private
   */
  _parseElementMetadata(element, options) {
    const isHTMLElement = element instanceof HTMLElement;
    const isString = typeof element === 'string';

    // If string, create temp element to parse attributes
    let tempEl = null;
    if (isString) {
      tempEl = document.createElement('div');
      tempEl.innerHTML = element;
      element = tempEl.firstElementChild;
      if (!element) {
        console.error('Invalid HTML string');
        return {};
      }
    }

    const get = (propName, attrName) => {
      return options[propName] || element[propName] || element?.getAttribute?.(attrName) || element?.dataset?.[propName];
    };

    const rawCategory = get('category', 'data-category');
    const rawGroup = get('group', 'data-group');
    const tabName = get('tab', 'data-tab');

    const metadata = {
      id: get('id', 'id') || get('name', 'data-name') || get('elementId', 'data-id') || Utilities.generateID(true),
      name: get('name', 'data-name'),
      menu: get('menu', 'data-menu'),
      tab: tabName,
      category: rawCategory ? this._generateCategoryId(tabName, rawCategory) : null,
      categoryRaw: rawCategory, // Keep original for display purposes
      categoryName: get('categoryName', 'data-category-name'),
      categoryIcon: get('categoryIcon', 'data-category-icon'),
      categoryCollapsed: get('categoryCollapsed', 'data-category-collapsed') === true || get('categoryCollapsed', 'data-category-collapsed') === 'true',
      group: rawGroup && rawCategory ? this._generateGroupId(tabName, rawCategory, rawGroup) : null,
      groupRaw: rawGroup, // Keep original for display purposes
      groupName: get('groupName', 'data-group-name'),
      groupIcon: get('groupIcon', 'data-group-icon'),
      groupCollapsed: get('groupCollapsed', 'data-group-collapsed') === true || get('groupCollapsed', 'data-group-collapsed') === 'true',
      atIndex: parseInt(get('atIndex', 'data-at-index')) || null,
      atAfter: get('atAfter', 'data-at-after'),
      atBefore: get('atBefore', 'data-at-before')
    };

    // If temp element was created, restore original string
    if (tempEl) {
      element = tempEl.innerHTML;
    }

    return metadata;
  }

  /**
   * Ensure category exists, create if not
   * @private
   */
  _ensureCategory(tabName, categoryId, config) {
    if (!this._categoryRegistry.has(tabName)) {
      this._categoryRegistry.set(tabName, new Map());
    }

    const tabCategories = this._categoryRegistry.get(tabName);

    if (!tabCategories.has(categoryId)) {
      // Category doesn't exist, create it
      const categoryConfig = {
        id: categoryId,
        name: config.name || config.rawName || categoryId.split('__').pop(), // Use raw name or extract from ID
        icon: config.icon || 'bx bx-folder',
        collapsible: true,
        collapsed: config.collapsed || false,
        groups: []
      };

      tabCategories.set(categoryId, categoryConfig);

      // Actually create the category in DOM if tab is active
      if (this.selectedTab === tabName) {
        this.addCategory(tabName, categoryConfig);
      }
    }

    return tabCategories.get(categoryId);
  }

  /**
   * Ensure group exists within a category, create if not
   * @private
   */
  _ensureGroup(tabName, categoryId, groupId, config) {
    if (!this._groupRegistry.has(categoryId)) {
      this._groupRegistry.set(categoryId, new Map());
    }

    const categoryGroups = this._groupRegistry.get(categoryId);

    if (!categoryGroups.has(groupId)) {
      // Group doesn't exist, create it
      const groupConfig = {
        id: groupId,
        name: config.name || config.rawName || groupId.split('__').pop(), // Use raw name or extract from ID
        icon: config.icon || 'bx bx-cube',
        collapsible: true,
        collapsed: config.collapsed || false
      };

      categoryGroups.set(groupId, groupConfig);

      // Actually create the group in DOM if tab is active
      if (this.selectedTab === tabName) {
        const categoryExists = this.getCategory(tabName, categoryId);
        if (categoryExists) {
          this.addGroupToCategory(tabName, categoryId, groupConfig);
        }
      }
    }

    return categoryGroups.get(groupId);
  }

  /**
   * Render a pending element to the DOM
   * @private
   */
  _renderElement(elementId) {
    const entry = this._elementRegistry.get(elementId);
    if (!entry || entry.rendered) return;

    const { element, metadata } = entry;
    const tabName = metadata.tab;
    const categoryId = metadata.category;
    const groupId = metadata.group;

    // Get target container
    let targetContainer;

    if (groupId && categoryId) {
      // Place in group
      const groupEl = this.getGroup(tabName, categoryId, groupId);
      if (groupEl) {
        targetContainer = groupEl.querySelector('.rd-group-content');
      }
    } else if (categoryId) {
      // Place directly in category
      const categoryEl = this.getCategory(tabName, categoryId);
      if (categoryEl) {
        targetContainer = categoryEl.querySelector('.rd-category-content');
      }
    } else {
      // Place directly in tab content
      targetContainer = this.getTabContent(tabName);
    }

    if (!targetContainer) {
      console.warn(`Target container not found for element ${elementId}`);
      return;
    }

    // Convert string to element if needed
    let domElement = element;
    if (typeof element === 'string') {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = element;
      domElement = wrapper.firstElementChild;
    }

    // Handle positioning (atIndex, atAfter, atBefore)
    if (metadata.atIndex !== null) {
      const children = Array.from(targetContainer.children);
      if (metadata.atIndex < children.length) {
        targetContainer.insertBefore(domElement, children[metadata.atIndex]);
      } else {
        targetContainer.appendChild(domElement);
      }
    } else if (metadata.atAfter) {
      const afterEl = targetContainer.querySelector(`[id="${metadata.atAfter}"]`);
      if (afterEl && afterEl.nextSibling) {
        targetContainer.insertBefore(domElement, afterEl.nextSibling);
      } else if (afterEl) {
        targetContainer.appendChild(domElement);
      } else {
        targetContainer.appendChild(domElement);
      }
    } else if (metadata.atBefore) {
      const beforeEl = targetContainer.querySelector(`[id="${metadata.atBefore}"]`);
      if (beforeEl) {
        targetContainer.insertBefore(domElement, beforeEl);
      } else {
        targetContainer.appendChild(domElement);
      }
    } else {
      targetContainer.appendChild(domElement);
    }

    // Mark as rendered
    entry.rendered = true;
    entry.domElement = domElement;

    // Add update-sidebar event listener
    domElement.addEventListener('update-sidebar', () => this._updateFromElement());

    // Call initialized lifecycle
    this._callLifecycle(domElement, 'initialized');

    // Setup visibility observer
    this._setupVisibilityObserver(elementId, domElement, categoryId, groupId);

    // Check if immediately visible
    this._checkElementVisibility(elementId);
  }

  /**
   * Setup visibility observer for an element
   * @private
   */
  _setupVisibilityObserver(elementId, domElement, categoryId, groupId) {
    const entry = this._elementRegistry.get(elementId);
    if (!entry) return;

    // Listen to category/group collapse events
    const checkVisibility = () => {
      this._checkElementVisibility(elementId);
    };

    // Store for cleanup
    if (!this._observedElements.has(domElement)) {
      this._observedElements.set(domElement, {
        categoryId,
        groupId,
        checkVisibility
      });
    }

    // Initial check
    setTimeout(() => checkVisibility(), 0);
  }

  /**
   * Check if element is currently visible
   * @private
   */
  _checkElementVisibility(elementId) {
    const entry = this._elementRegistry.get(elementId);
    if (!entry || !entry.rendered) return;

    const { domElement, metadata } = entry;
    const tabName = metadata.tab;
    const categoryId = metadata.category;
    const groupId = metadata.group;

    // Check tab visibility
    const tabVisible = this.selectedTab === tabName;
    if (!tabVisible) {
      this._updateElementVisibility(elementId, false);
      return;
    }

    // Check category collapse state
    if (categoryId) {
      const categoryCollapsed = this._collapsedCategories.get(tabName)?.has(categoryId);
      if (categoryCollapsed) {
        this._updateElementVisibility(elementId, false);
        return;
      }
    }

    // Check group collapse state
    if (groupId && categoryId) {
      const groupCollapsed = this._collapsedCategoryGroups.get(tabName)?.get(categoryId)?.has(groupId);
      if (groupCollapsed) {
        this._updateElementVisibility(elementId, false);
        return;
      }
    }

    // Element is visible
    this._updateElementVisibility(elementId, true);
  }

  /**
   * Update element visibility state and call lifecycle
   * @private
   */
  _updateElementVisibility(elementId, visible) {
    const entry = this._elementRegistry.get(elementId);
    if (!entry) return;

    const wasVisible = entry.visible;
    entry.visible = visible;
    // Call visibilityChanged lifecycle
    if (wasVisible !== visible && entry.domElement) {
      this._callLifecycle(entry.domElement, 'visibilityChanged', visible);
    }

    // Call onCreated lifecycle on first visibility
    if (visible && !entry.created && entry.domElement.visible === true) {
      entry.created = true;
      entry.domElement.created = true;
      this._callLifecycle(entry.domElement, 'onCreated');
    }
    if (entry.domElement?.visibilityChanged && typeof entry.domElement.visibilityChanged === 'function') {
      entry.domElement.visibilityChanged(!visible);
    }
  }

  /**
   * Call a lifecycle method on an element
   * @private
   */
  _callLifecycle(domElement, methodName, ...args) {
    try {
      // Check for rd object
      if (domElement.rd && typeof domElement.rd[methodName] === 'function') {
        // Call with domElement as 'this' context
        domElement.rd[methodName].call(domElement, ...args);
      }

      // Also dispatch custom event
      const eventName = `rd-${methodName.toLowerCase()}`;
      domElement.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { args },
          bubbles: false
        })
      );
    } catch (error) {
      console.error(`Error calling lifecycle ${methodName}:`, error);
    }
  }

  /**
   * Remove an element from the sidebar
   * @param {string} elementId - Element ID to remove
   */
  removeElement(elementId) {
    const entry = this._elementRegistry.get(elementId);
    if (!entry) return;

    // Remove from DOM if rendered
    if (entry.rendered && entry.domElement) {
      entry.domElement.remove();
    }

    // Remove from tracking
    const metadata = entry.metadata;

    if (metadata.category) {
      const categoryElements = this._elementsByCategory.get(metadata.category);
      if (categoryElements) {
        const index = categoryElements.indexOf(elementId);
        if (index > -1) categoryElements.splice(index, 1);
      }
    }

    if (metadata.group) {
      const groupElements = this._elementsByGroup.get(metadata.group);
      if (groupElements) {
        const index = groupElements.indexOf(elementId);
        if (index > -1) groupElements.splice(index, 1);
      }
    }

    // Remove from pending
    if (this._pendingElements.has(metadata.tab)) {
      const pending = this._pendingElements.get(metadata.tab);
      const index = pending.indexOf(elementId);
      if (index > -1) pending.splice(index, 1);
    }

    // Remove from registry
    this._elementRegistry.delete(elementId);
  }

  /**
   * Get the content container for a specific tab
   * @param {string} tabName - Name of the tab
   * @returns {HTMLElement|null}
   */
  getTabContent(tabName) {
    return this._contentContainers.get(tabName) || null;
  }

  /**
   * Remove content for a specific tab
   * @param {string} tabName - Name of the tab
   */
  removeTabContent(tabName) {
    const container = this._contentContainers.get(tabName);
    if (container) {
      container.remove();
      this._contentContainers.delete(tabName);
    }
    // Clean up category collapse states
    this._collapsedCategories.delete(tabName);
    this._collapsedCategoryGroups.delete(tabName);
  }

  /**
   * Add a category to a tab's content
   * @param {string} tabName - Name of the tab
   * @param {Object} category - Category configuration
   * @param {string} category.id - Unique category ID
   * @param {string} category.name - Category display name
   * @param {string} [category.icon] - Optional icon class (e.g., 'bx bx-folder')
   * @param {boolean} [category.collapsible=true] - Whether category can be collapsed
   * @param {boolean} [category.collapsed=false] - Initial collapsed state
   * @param {Array} [category.groups] - Array of group objects
   * @param {HTMLElement|string} [category.content] - Direct content (if no groups)
   * @returns {HTMLElement} The created category element
   */
  addCategory(tabName, category) {
    const container = this.getTabContent(tabName);
    if (!container) {
      console.error(`Tab "${tabName}" not found. Create tab content first.`);
      return null;
    }

    const categoryId = category.id || `cat_${Date.now()}`;
    const collapsible = category.collapsible !== false;
    const collapsed = category.collapsed === true;

    // Track collapse state
    if (!this._collapsedCategories.has(tabName)) {
      this._collapsedCategories.set(tabName, new Set());
    }
    if (collapsed) {
      this._collapsedCategories.get(tabName).add(categoryId);
    }

    // Create category element
    const categoryEl = document.createElement('div');
    categoryEl.className = 'rd-content-category';
    categoryEl.setAttribute('data-category-id', categoryId);
    if (collapsed) {
      categoryEl.classList.add('collapsed');
    }

    // Create header
    const header = document.createElement('div');
    header.className = 'rd-category-header';
    if (collapsible) {
      header.classList.add('collapsible');
      header.onclick = () => this.toggleCategory(tabName, categoryId);
    }

    if (category.icon) {
      const icon = document.createElement('i');
      icon.className = category.icon;
      header.appendChild(icon);
    }

    const title = document.createElement('span');
    title.className = 'rd-category-title';
    title.textContent = category.name;
    header.appendChild(title);

    if (collapsible) {
      const chevron = document.createElement('i');
      chevron.className = 'bx bx-chevron-down rd-category-chevron';
      header.appendChild(chevron);
    }

    categoryEl.appendChild(header);

    // Create content container
    const contentContainer = document.createElement('div');
    contentContainer.className = 'rd-category-content';

    // Add groups if provided
    if (category.groups && Array.isArray(category.groups)) {
      category.groups.forEach((group) => {
        const groupEl = this._createGroupElement(tabName, categoryId, group);
        contentContainer.appendChild(groupEl);
      });
    }

    // Add direct content if provided
    if (category.content) {
      if (category.content instanceof HTMLElement) {
        contentContainer.appendChild(category.content);
      } else if (typeof category.content === 'string') {
        const contentDiv = document.createElement('div');
        contentDiv.className = 'rd-category-direct-content';
        contentDiv.innerHTML = category.content;
        contentContainer.appendChild(contentDiv);
      }
    }

    categoryEl.appendChild(contentContainer);
    container.appendChild(categoryEl);

    return categoryEl;
  }

  /**
   * Create a group element
   * @private
   */
  _createGroupElement(tabName, categoryId, group) {
    const groupId = group.id || `grp_${Date.now()}`;
    const collapsible = group.collapsible !== false;
    const collapsed = group.collapsed === true;

    // Track collapse state
    if (!this._collapsedCategoryGroups.has(tabName)) {
      this._collapsedCategoryGroups.set(tabName, new Map());
    }
    if (!this._collapsedCategoryGroups.get(tabName).has(categoryId)) {
      this._collapsedCategoryGroups.get(tabName).set(categoryId, new Set());
    }
    if (collapsed) {
      this._collapsedCategoryGroups.get(tabName).get(categoryId).add(groupId);
    }

    // Create group element
    const groupEl = document.createElement('div');
    groupEl.className = 'rd-content-group';
    groupEl.setAttribute('data-group-id', groupId);
    if (collapsed) {
      groupEl.classList.add('collapsed');
    }

    // Create header (if group has a name)
    if (group.name) {
      const header = document.createElement('div');
      header.className = 'rd-group-header';
      if (collapsible) {
        header.classList.add('collapsible');
        header.onclick = () => this.toggleGroup(tabName, categoryId, groupId);
      }

      if (group.icon) {
        const icon = document.createElement('i');
        icon.className = group.icon;
        header.appendChild(icon);
      }

      const title = document.createElement('span');
      title.className = 'rd-group-title';
      title.textContent = group.name;
      header.appendChild(title);

      if (collapsible) {
        const chevron = document.createElement('i');
        // Set initial icon based on collapsed state
        chevron.className = collapsed ? 'bx bx-plus rd-group-chevron' : 'bx bx-minus rd-group-chevron';
        header.appendChild(chevron);
      }

      groupEl.appendChild(header);
    }

    // Create content container
    const contentContainer = document.createElement('div');
    contentContainer.className = 'rd-group-content';

    if (group.content) {
      if (group.content instanceof HTMLElement) {
        contentContainer.appendChild(group.content);
      } else if (typeof group.content === 'string') {
        contentContainer.innerHTML = group.content;
      }
    }

    groupEl.appendChild(contentContainer);

    return groupEl;
  }

  /**
   * Toggle category collapse state
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   */
  toggleCategory(tabName, categoryId) {
    const container = this.getTabContent(tabName);
    if (!container) return;

    const categoryEl = container.querySelector(`[data-category-id="${categoryId}"]`);
    if (!categoryEl) return;

    const collapseSet = this._collapsedCategories.get(tabName);
    const isCollapsed = collapseSet && collapseSet.has(categoryId);

    if (isCollapsed) {
      collapseSet.delete(categoryId);
      categoryEl.classList.remove('collapsed');
    } else {
      if (!collapseSet) {
        this._collapsedCategories.set(tabName, new Set([categoryId]));
      } else {
        collapseSet.add(categoryId);
      }
      categoryEl.classList.add('collapsed');
    }

    // Update visibility for elements in this category
    if (this._elementsByCategory.has(categoryId)) {
      this._elementsByCategory.get(categoryId).forEach((elementId) => {
        this._checkElementVisibility(elementId);
      });
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('category-toggle', {
        detail: { tabName, categoryId, collapsed: !isCollapsed }
      })
    );
  }

  /**
   * Collapse or expand a category
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID (can be raw name or full scoped ID)
   * @param {boolean} collapse - True to collapse, false to expand
   */
  collapseCategory(tabName, categoryId, collapse) {
    // Generate scoped ID if raw category name was provided
    const scopedCategoryId = categoryId.includes('__') ? categoryId : this._generateCategoryId(tabName, categoryId);

    const container = this.getTabContent(tabName);
    if (!container) return;

    const categoryEl = container.querySelector(`[data-category-id="${scopedCategoryId}"]`);
    if (!categoryEl) return;

    const collapseSet = this._collapsedCategories.get(tabName);
    const isCurrentlyCollapsed = collapseSet && collapseSet.has(scopedCategoryId);

    // Only proceed if state needs to change
    if (collapse === isCurrentlyCollapsed) return;

    if (collapse) {
      // Collapse the category
      if (!collapseSet) {
        this._collapsedCategories.set(tabName, new Set([scopedCategoryId]));
      } else {
        collapseSet.add(scopedCategoryId);
      }
      categoryEl.classList.add('collapsed');
    } else {
      // Expand the category
      if (collapseSet) {
        collapseSet.delete(scopedCategoryId);
      }
      categoryEl.classList.remove('collapsed');
    }

    // Update visibility for elements in this category
    if (this._elementsByCategory.has(scopedCategoryId)) {
      this._elementsByCategory.get(scopedCategoryId).forEach((elementId) => {
        this._checkElementVisibility(elementId);
      });
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('category-toggle', {
        detail: { tabName, categoryId: scopedCategoryId, collapsed: collapse }
      })
    );
  }

  collapseAllCategories(tabName) {
    const container = this.getTabContent(tabName);
    if (!container) return;
    let allCategoryIds = this._categoryRegistry.get(tabName);
    if (!allCategoryIds) return;
    allCategoryIds.forEach((category, categoryId) => {
      this.collapseCategory(tabName, categoryId, true);
    });
  }

  /**
   * Toggle group collapse state
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   * @param {string} groupId - Group ID
   */
  toggleGroup(tabName, categoryId, groupId) {
    const container = this.getTabContent(tabName);
    if (!container) return;

    const groupEl = container.querySelector(`[data-group-id="${groupId}"]`);
    if (!groupEl) return;

    const categoryGroups = this._collapsedCategoryGroups.get(tabName)?.get(categoryId);
    const isCollapsed = categoryGroups && categoryGroups.has(groupId);

    // Get the chevron icon
    const chevron = groupEl.querySelector('.rd-group-chevron');

    if (isCollapsed) {
      categoryGroups.delete(groupId);
      groupEl.classList.remove('collapsed');
      // Change plus to minus
      if (chevron) {
        chevron.classList.remove('bx-plus');
        chevron.classList.add('bx-minus');
      }
    } else {
      if (!this._collapsedCategoryGroups.has(tabName)) {
        this._collapsedCategoryGroups.set(tabName, new Map());
      }
      if (!this._collapsedCategoryGroups.get(tabName).has(categoryId)) {
        this._collapsedCategoryGroups.get(tabName).set(categoryId, new Set());
      }
      this._collapsedCategoryGroups.get(tabName).get(categoryId).add(groupId);
      groupEl.classList.add('collapsed');
      // Change minus to plus
      if (chevron) {
        chevron.classList.remove('bx-minus');
        chevron.classList.add('bx-plus');
      }
    }

    // Update visibility for elements in this group
    if (this._elementsByGroup.has(groupId)) {
      this._elementsByGroup.get(groupId).forEach((elementId) => {
        this._checkElementVisibility(elementId);
      });
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('group-toggle', {
        detail: { tabName, categoryId, groupId, collapsed: !isCollapsed }
      })
    );
  }

  /**
   * Collapse or expand a group
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID (can be raw name or full scoped ID)
   * @param {string} groupId - Group ID (can be raw name or full scoped ID)
   * @param {boolean} collapse - True to collapse, false to expand
   */
  collapseGroup(tabName, categoryId, groupId, collapse) {
    // Generate scoped IDs if raw names were provided
    const scopedCategoryId = categoryId.includes('__') ? categoryId : this._generateCategoryId(tabName, categoryId);
    const scopedGroupId = groupId.includes('__') ? groupId : this._generateGroupId(tabName, categoryId, groupId);

    const container = this.getTabContent(tabName);
    if (!container) return;

    const groupEl = container.querySelector(`[data-group-id="${scopedGroupId}"]`);
    if (!groupEl) return;

    const categoryGroups = this._collapsedCategoryGroups.get(tabName)?.get(scopedCategoryId);
    const isCurrentlyCollapsed = categoryGroups && categoryGroups.has(scopedGroupId);

    // Only proceed if state needs to change
    if (collapse === isCurrentlyCollapsed) return;

    // Get the chevron icon
    const chevron = groupEl.querySelector('.rd-group-chevron');

    if (collapse) {
      // Collapse the group
      if (!this._collapsedCategoryGroups.has(tabName)) {
        this._collapsedCategoryGroups.set(tabName, new Map());
      }
      if (!this._collapsedCategoryGroups.get(tabName).has(scopedCategoryId)) {
        this._collapsedCategoryGroups.get(tabName).set(scopedCategoryId, new Set());
      }
      this._collapsedCategoryGroups.get(tabName).get(scopedCategoryId).add(scopedGroupId);
      groupEl.classList.add('collapsed');
      // Change minus to plus
      if (chevron) {
        chevron.classList.remove('bx-minus');
        chevron.classList.add('bx-plus');
      }
    } else {
      // Expand the group
      if (categoryGroups) {
        categoryGroups.delete(scopedGroupId);
      }
      groupEl.classList.remove('collapsed');
      // Change plus to minus
      if (chevron) {
        chevron.classList.remove('bx-plus');
        chevron.classList.add('bx-minus');
      }
    }

    // Update visibility for elements in this group
    if (this._elementsByGroup.has(scopedGroupId)) {
      this._elementsByGroup.get(scopedGroupId).forEach((elementId) => {
        this._checkElementVisibility(elementId);
      });
    }

    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('group-toggle', {
        detail: { tabName, categoryId: scopedCategoryId, groupId: scopedGroupId, collapsed: collapse }
      })
    );
  }

  /**
   * Add a group to an existing category
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   * @param {Object} group - Group configuration
   * @returns {HTMLElement} The created group element
   */
  addGroupToCategory(tabName, categoryId, group) {
    const container = this.getTabContent(tabName);
    if (!container) {
      console.error(`Tab "${tabName}" not found.`);
      return null;
    }

    const categoryEl = container.querySelector(`[data-category-id="${categoryId}"]`);
    if (!categoryEl) {
      console.error(`Category "${categoryId}" not found in tab "${tabName}".`);
      return null;
    }

    const contentContainer = categoryEl.querySelector('.rd-category-content');
    if (!contentContainer) {
      console.error(`Category content container not found.`);
      return null;
    }

    const groupEl = this._createGroupElement(tabName, categoryId, group);
    contentContainer.appendChild(groupEl);

    return groupEl;
  }

  /**
   * Remove a category from a tab
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   */
  removeCategory(tabName, categoryId) {
    const container = this.getTabContent(tabName);
    if (!container) return;

    const categoryEl = container.querySelector(`[data-category-id="${categoryId}"]`);
    if (categoryEl) {
      categoryEl.remove();

      // Clean up collapse states
      this._collapsedCategories.get(tabName)?.delete(categoryId);
      this._collapsedCategoryGroups.get(tabName)?.delete(categoryId);
    }
  }

  /**
   * Remove a group from a category
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   * @param {string} groupId - Group ID
   */
  removeGroup(tabName, categoryId, groupId) {
    const container = this.getTabContent(tabName);
    if (!container) return;

    const groupEl = container.querySelector(`[data-group-id="${groupId}"]`);
    if (groupEl) {
      groupEl.remove();

      // Clean up collapse state
      this._collapsedCategoryGroups.get(tabName)?.get(categoryId)?.delete(groupId);
    }
  }

  /**
   * Get all categories for a tab
   * @param {string} tabName - Name of the tab
   * @returns {Array<HTMLElement>}
   */
  getCategories(tabName) {
    const container = this.getTabContent(tabName);
    if (!container) return [];

    return Array.from(container.querySelectorAll('.rd-content-category'));
  }

  /**
   * Get a specific category element
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   * @returns {HTMLElement|null}
   */
  getCategory(tabName, categoryId) {
    const container = this.getTabContent(tabName);
    if (!container) return null;

    return container.querySelector(`[data-category-id="${categoryId}"]`);
  }

  /**
   * Get a specific group element
   * @param {string} tabName - Name of the tab
   * @param {string} categoryId - Category ID
   * @param {string} groupId - Group ID
   * @returns {HTMLElement|null}
   */
  getGroup(tabName, categoryId, groupId) {
    const categoryEl = this.getCategory(tabName, categoryId);
    if (!categoryEl) return null;

    return categoryEl.querySelector(`[data-group-id="${groupId}"]`);
  }

  /**
   * Remove content for a specific tab
   * @param {string} tabName - Name of the tab
   */
  removeTabContent(tabName) {
    const container = this._contentContainers.get(tabName);
    if (container) {
      container.remove();
      this._contentContainers.delete(tabName);
    }
  }

  /**
   * Clear all tab content
   */
  clearAllTabContent() {
    this._contentContainers.forEach((container) => container.remove());
    this._contentContainers.clear();
  }

  /**
   * Show loader with optional message
   * @param {string} message - Optional loading message
   */
  showLoader(message = 'Loading...') {
    this.loadingMessage = message;
    this.loading = true;
  }

  /**
   * Hide loader
   */
  hideLoader() {
    this.loading = false;
  }

  /**
   * Execute a function with loader displayed
   * @param {Function} fn - Function to execute (can be async)
   * @param {string} message - Optional loading message
   * @returns {Promise<any>} Result of the function
   */
  async withLoader(fn, message = 'Loading...') {
    this.showLoader(message);
    try {
      const result = await fn();
      return result;
    } finally {
      this.hideLoader();
    }
  }

  /**
   * Toggle group collapse state
   * @param {string} position - 'top' | 'middle' | 'bottom'
   */
  toggleGroupCollapse(position) {
    if (this._collapsedGroups.has(position)) {
      this._collapsedGroups.delete(position);
    } else {
      this._collapsedGroups.add(position);
    }
    this.requestUpdate();
  }

  /**
   * Show search box for a tab
   * @param {string} tabName - Name of the tab
   */
  showSearch(tabName) {
    this._searchVisible.set(tabName, true);
    this.requestUpdate();
  }

  /**
   * Hide search box for a tab
   * @param {string} tabName - Name of the tab
   */
  hideSearch(tabName) {
    this._searchVisible.set(tabName, false);
    this.requestUpdate();
  }

  /**
   * Get search value for a tab
   * @param {string} tabName - Name of the tab
   * @returns {string}
   */
  getSearchValue(tabName) {
    return this._searchValues.get(tabName) || '';
  }

  /**
   * Close a tab
   * @param {string} tabName - Name of the tab to close
   */
  closeTab(tabName) {
    // Call callback to check if close should proceed
    const shouldClose = this.onTabClose(tabName);
    if (shouldClose === false) return;

    // Remove tab from tabs array
    this.tabs = this.tabs.filter((t) => t.name !== tabName);

    // Clean up content
    this.removeTabContent(tabName);

    // If this was the selected tab, select another
    if (this.selectedTab === tabName) {
      const remainingTabs = this.tabs.filter((t) => t.menuName === this.menuSelected && !t.hidden);
      this.selectedTab = remainingTabs.length > 0 ? remainingTabs[0].name : '';
    }

    this.requestUpdate();
  }

  /**
   * Update content visibility based on selected tab
   * @private
   */
  _updateContentVisibility() {
    const tabsPanel = this.querySelector('.tabs-panel');
    if (!tabsPanel) return;

    const contentArea = tabsPanel.querySelector('.rd-sidebar-content-area');
    if (!contentArea) return;

    const hasContent = this._contentContainers.size > 0;
    const hasSelectedTab = this.selectedTab && this.menuSelected;

    // Show/hide content area
    contentArea.style.display = hasSelectedTab && hasContent ? 'block' : 'none';

    // Show only the active tab's content
    this._contentContainers.forEach((container, tabName) => {
      container.style.display = tabName === this.selectedTab ? 'block' : 'none';
    });

    // Render pending elements for the now-visible tab
    if (this.selectedTab && this._pendingElements.has(this.selectedTab)) {
      const pendingIds = [...this._pendingElements.get(this.selectedTab)];
      this._pendingElements.delete(this.selectedTab);

      // Ensure categories and groups exist first
      pendingIds.forEach((elementId) => {
        const entry = this._elementRegistry.get(elementId);
        if (entry && !entry.rendered) {
          const { metadata } = entry;

          // Ensure category exists
          if (metadata.category) {
            const catConfig = this._categoryRegistry.get(this.selectedTab)?.get(metadata.category);
            if (catConfig) {
              const existingCat = this.getCategory(this.selectedTab, metadata.category);
              if (!existingCat) {
                this.addCategory(this.selectedTab, catConfig);
              }
            }
          }

          // Ensure group exists
          if (metadata.group && metadata.category) {
            const groupConfig = this._groupRegistry.get(metadata.category)?.get(metadata.group);
            if (groupConfig) {
              const existingGroup = this.getGroup(this.selectedTab, metadata.category, metadata.group);
              if (!existingGroup) {
                this.addGroupToCategory(this.selectedTab, metadata.category, groupConfig);
              }
            }
          }
        }
      });

      // Now render all elements
      pendingIds.forEach((elementId) => {
        this._renderElement(elementId);
      });
    }

    // Update visibility for all rendered elements
    this._elementRegistry.forEach((entry, elementId) => {
      if (entry.rendered) {
        this._checkElementVisibility(elementId);
      }
    });
  }

  _getMenusByPosition(position) {
    if (!this.menus || !Array.isArray(this.menus)) return [];
    return this.menus.filter((menu) => !menu.hidden && (menu.position === position || (!menu.position && position === 'middle')));
  }

  /**
   * Check if a menu has any tabs with unsaved changes
   * @param {string} menuName - Name of the menu
   * @returns {boolean}
   * @private
   */
  _menuHasChanges(menuName) {
    if (!this.tabs || !Array.isArray(this.tabs)) return false;
    return this.tabs.some((tab) => tab.menuName === menuName && tab.isChanged === true);
  }

  _renderMenuIcon(menu) {
    const isSelected = this.menuSelected === menu.name;
    const hasChanges = this._menuHasChanges(menu.name);
    const classes = {
      'menu-icon': true,
      selected: isSelected,
      disabled: menu.disabled,
      hoverable: menu.hoverable !== false
    };

    const classList = Object.keys(classes)
      .filter((k) => classes[k])
      .join(' ');
    const tooltip = menu.tooltip || '';
    const notification = menu.notification;

    return html`
      <div class="${classList}" @click=${() => this._handleMenuClick(menu)} title="${tooltip}" tip-placement="right">
        ${menu.icon ? html`${unsafeHTML(menu.icon)}` : ''}
        ${menu.badge && !notification ? html`<span class="menu-badge">${menu.badge}</span>` : ''}
        ${hasChanges && !notification ? html`<span class="change-dot"></span>` : ''}
        ${notification ? this._renderNotification(notification) : ''}
      </div>
    `;
  }

  _renderTab(tab) {
    const isSelected = this.selectedTab === tab.name;
    const classes = {
      'tab-item': true,
      selected: isSelected,
      disabled: tab.disabled
    };

    const classList = Object.keys(classes)
      .filter((k) => classes[k])
      .join(' ');
    const tooltip = tab.tooltip || '';
    const notification = tab.notification;

    return html`
      <!-- Tab Header (no wrapper needed) -->
      <div class="${classList}" @click=${() => this._handleTabClick(tab)} title="${tooltip}">
        ${tab.icon ? html`${unsafeHTML(tab.icon)}` : ''}
        <span class="tab-label">${tab.text || tab.name}</span>
        ${tab.isChanged && !notification ? html`<span class="change-dot" title="Modified"></span>` : ''}
        ${notification ? this._renderNotification(notification) : ''}

        <!-- Custom Actions -->
        ${tab.actions && tab.actions.length > 0 ? html`
          <div class="tab-actions" @click=${(e) => e.stopPropagation()}>
            ${tab.actions.map((action) => this._renderAction(action, tab))}
          </div> ` : ''}

        <!-- Close Button -->
        ${tab.closeable
          ? html`
            <button
              class="tab-close-btn"
              @click=${(e) => {
                e.stopPropagation();
                this.closeTab(tab.name);
              }}
              title="Close tab"
            >
              <i class="bx bx-x"></i>
            </button>
          `
          : ''}
      </div>
    `;
  }

  _renderSearchBox(selectedTab) {
    if (!selectedTab) return '';

    const showSearch = this._searchVisible.get(selectedTab.name) || (selectedTab.searchable && this.selectedTab === selectedTab.name);
    const inputRef = createRef();
    if (!showSearch) return '';

    return html`
      <div class="tab-search">
        <i class="bx bx-search"></i>
        <input
          ${ref(inputRef)}
          type="text"
          placeholder="${selectedTab.searchPlaceholder || 'Search...'}"
          .value="${this._searchValues.get(selectedTab.name) || ''}"
          @input=${(e) => {
            const query = e.target.value;
            this._searchValues.set(selectedTab.name, query);

            // Inline callback (tree API)
            const def = this._treeTabs?.get(selectedTab.name);
            def?.onSearch?.(query, def);

            // Global events — both legacy name and tree-API name
            const detail = { tabName: selectedTab.name, tabId: selectedTab.name, value: query };
            this.dispatchEvent(new CustomEvent('tab-search',    { detail }));
            this.dispatchEvent(new CustomEvent('rd-tab-search', { bubbles: true, composed: true, detail }));
          }}
        />
        <button
          class="search-close-btn"
          @click=${() => {
            if (!inputRef.value) return;
            if (inputRef.value.value === '') return;
            inputRef.value.value = '';
            this._searchValues.set(selectedTab.name, '');

            const def = this._treeTabs?.get(selectedTab.name);
            def?.onSearch?.('', def);

            const detail = { tabName: selectedTab.name, tabId: selectedTab.name, value: '' };
            this.dispatchEvent(new CustomEvent('tab-search',    { detail }));
            this.dispatchEvent(new CustomEvent('rd-tab-search', { bubbles: true, composed: true, detail }));
          }}
          title="Clear search"
        >
          <i class="bx bx-x"></i>
        </button>
      </div>
    `;
  }

  _renderAction(action, tab) {
    // Simple button that triggers onClick callback
    // User can implement their own dropdown/menu logic in the callback
    return html`
      <button
        class="action-btn"
        @click=${(e) => {
          e.stopPropagation();
          if (action.onClick) {
            action.onClick(action, tab, e);
          }
        }}
        title="${action.tooltip || ''}"
      >
        ${action.icon ? html`${unsafeHTML(action.icon)}` : ''} ${action.label ? html`<span>${action.label}</span>` : ''}
      </button>
    `;
  }

  _renderNotification(notification) {
    const notificationColors = {
      info: '#3b82f6',
      warning: '#f59e0b',
      error: '#ef4444',
      success: '#10b981',
      custom: notification.color || '#3b82f6'
    };

    const color = notificationColors[notification.type] || notificationColors.info;
    const count = notification.count;

    return html`
      <span class="notification-dot ${notification.type}"
            style="${notification.type === 'custom' && notification.color ? `background: ${notification.color};` : ''}" ">
      ${count ? html`<span class="notification-count"></span>` : ''}
      </span>
    `;
    //removed ${count}, use batch instead. title="${notification.type}: ${count || ''}
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    // Force update search input value when tab changes
    if (changedProperties.has('selectedTab')) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        const searchInput = this.querySelector('.tab-search input');
        if (searchInput) {
          const currentValue = this._searchValues.get(this.selectedTab) || '';
          searchInput.value = currentValue;
        }
      }, 0);

      // Trigger content visibility update when tab changes
      this._updateContentVisibility();
    }

    // Validate and clean up menus
    if (changedProperties.has('menus')) {
      const menuExists = this.menus.some((m) => m.name === this.menuSelected && !m.hidden);
      if (!menuExists && this.menuSelected) {
        console.warn(`Selected menu "${this.menuSelected}" no longer exists or is hidden. Clearing selection.`);
        this.menuSelected = '';
        this.selectedTab = '';
        this._updateContentVisibility();
      }
    }

    // Validate and clean up tabs + orphaned content
    if (changedProperties.has('tabs')) {
      const tabExists = this.tabs.some((t) => t.name === this.selectedTab && !t.hidden);
      if (!tabExists && this.selectedTab) {
        console.warn(`Selected tab "${this.selectedTab}" no longer exists or is hidden. Clearing selection.`);
        this.selectedTab = '';
        this._updateContentVisibility();
      }

      // Clean up orphaned content (content for tabs that no longer exist)
      const tabNames = new Set(this.tabs.map((t) => t.name));
      this._contentContainers.forEach((container, tabName) => {
        if (!tabNames.has(tabName)) {
          if (container && container.parentNode) {
            container.remove(); // Remove from DOM
          }
          this._contentContainers.delete(tabName); // Remove from Map
          console.info(`Cleaned up orphaned content for removed tab: "${tabName}"`);
        }
      });
    }
  }

  firstUpdated() {
    // After first render, check if we have pending elements for the selected tab
    if (this.selectedTab) {
      setTimeout(() => {
        this._updateContentVisibility();
      }, 0);
    }
  }

  _renderMenuGroup(position, label) {
    const menus = this._getMenusByPosition(position);
    if (menus.length === 0) return '';

    const isCollapsed = this._collapsedGroups.has(position);

    if (!this.groupsCollapsible) {
      // No grouping, just render menus
      return html`
        <div class="menu-section menu-${position}">${menus.map((menu) => this._renderMenuIcon(menu))}</div> `;
    }

    return html`
      <div class="menu-section menu-${position}">
        <div class="menu-group-header ${isCollapsed ? 'collapsed' : ''}"
             @click=${() => this.toggleGroupCollapse(position)}>
            <!-- <span class="group-label">${label}</span> -->
          <i class="bx bx-chevron-${isCollapsed ? 'down' : 'up'}"></i>
        </div>
        ${!isCollapsed ? html`
          <div class="menu-group-content">${menus.map((menu) => this._renderMenuIcon(menu))}</div> ` : ''}
      </div>
    `;
  }

  render() {
    const hasSelectedMenu = this.menuSelected !== '';
    const selectedMenuTabs = this.tabs && Array.isArray(this.tabs) ? this.tabs.filter((tab) => !tab.hidden && (tab.menuName === this.menuSelected || !tab.menuName)) : [];

    return html`
      <div class="sidebar-container ${this.loading ? 'loading' : ''}"
           ${this.keyboardNavigationEnabled ? 'tabindex="0"' : ''}>
        <!-- Loader Overlay -->
        ${this.loading
          ? html`
            <div class="rd-sidebar-loader">
              <div class="rd-loader-spinner"></div>
              <div class="rd-loader-message">${this.loadingMessage}</div>
            </div>
          `
          : ''}

        <!-- Menu Bar (Left Side) -->
        <div class="menu-bar">
          <!-- Logo Section -->
          ${this.logo
            ? html`
              <div class="logo-section" @click=${this._handleLogoClick}>
                <img src="${this.logo}" alt="Logo" class="logo-image" />
              </div>
            `
            : ''}

          <!-- Top Menus -->
          ${this._renderMenuGroup('top', 'Top')}

          <!-- Middle Menus -->
          ${this._renderMenuGroup('middle', 'Middle')}

          <!-- Bottom Menus -->
          ${this._renderMenuGroup('bottom', 'Bottom')}
        </div>

        <!-- Tabs Panel (Right Side - Expandable) -->
        <div class="tabs-panel ${hasSelectedMenu ? 'expanded' : ''}">
          <!-- Collapse Indicator (Elementor-style) -->
          ${hasSelectedMenu
            ? html`
              <div class="collapse-indicator"
                   @click=${() => this._handleMenuClick({ name: this.menuSelected, collapse: true })} title="">
                <i class="bx bx-chevron-left"></i>
              </div>
            `
            : ''}

          <div class="tabs-list">${selectedMenuTabs.map((tab) => this._renderTab(tab))}</div>

          <!-- Search Box (appears below all tabs if any tab is searchable and selected) -->
          ${this._renderSearchBox(selectedMenuTabs.find((t) => t.name === this.selectedTab))}
        </div>
      </div>
    `;
  }
} // end class RdSideBar

// ═══════════════════════════════════════════════════════════════════════════
//  Tree Navigation API  (non-breaking extension)
//
//  registerTab()      \u2013 declare a tab with inline + global event support
//  addGroup()         \u2013 add a (nested) collapsible group under any tab/group
//  addItem()          \u2013 add a leaf element into a group
//  setGroupContent()  \u2013 set a custom content panel for a group
//  clearGroupItems()  \u2013 remove all leaf items from a group (before re-populate)
//
//  Global events fired on the sidebar element:
//    rd-tab-select    { tabId, tab }
//    rd-tab-deselect  { tabId, tab }
//    rd-group-expand  { groupId, group, tabId, path }
//    rd-group-collapse{ groupId, group, tabId, path }
// ═══════════════════════════════════════════════════════════════════════════

Object.assign(RdSideBar.prototype, {

  // ── Public API ─────────────────────────────────────────────────────────

  /**
   * Register a tab with the tree API.
   *
   * @param {{
   *   id, menuName, label, icon?,
   *   searchable?, searchPlaceholder?,
   *   onSelect?, onDeselect?,
   *   onSearch?
   * }} options
   */
  registerTab(options = {}) {
    const {
      id, menuName, label, icon = '',
      searchable = false, searchPlaceholder = 'Search…',
      onSelect, onDeselect, onSearch,
    } = options;
    if (!id || !menuName) throw new Error('[Sidebar.registerTab] id and menuName are required');

    this._treeTabs.set(id, { id, menuName, label, icon, searchable, searchPlaceholder, onSelect, onDeselect, onSearch });
    this._treeTabRoots.set(id, []);

    // Patch or add the tab in sidebar.tabs with searchable flag
    const existing = this.tabs.find(t => t.name === id);
    if (!existing) {
      this.tabs = [...this.tabs, { name: id, text: label, menuName, icon, searchable, searchPlaceholder }];
    } else {
      this.tabs = this.tabs.map(t =>
        t.name === id ? { ...t, searchable, searchPlaceholder } : t
      );
    }

    return { id };
  },

  /**
   * Add a collapsible group (or nested subgroup) to the sidebar tree.
   *
   * @param {{
   *   id, tabId, parentId?, label, icon?,
   *   collapsed?, onExpand?, onCollapse?, onClick?
   * }} options
   */
  addGroup(options = {}) {
    const {
      id, tabId, parentId = null,
      label = '', icon = '',
      collapsed = true,
      noHeader = false,   // true = no collapsible header, body always visible
      onExpand, onCollapse, onClick,
    } = options;
    if (!id || !tabId) throw new Error('[Sidebar.addGroup] id and tabId are required');

    this._treeGroups.set(id, {
      id, tabId, parentId, label, icon, noHeader,
      children: [],
      onExpand, onCollapse, onClick,
    });

    // noHeader groups are always expanded
    if (!collapsed || noHeader) this._treeExpanded.add(id);

    if (parentId) {
      const parent = this._treeGroups.get(parentId);
      if (parent && !parent.children.includes(id)) parent.children.push(id);
    } else {
      const roots = this._treeTabRoots.get(tabId) ?? [];
      if (!roots.includes(id)) this._treeTabRoots.set(tabId, [...roots, id]);
    }

    this._rebuildTabTree(tabId);
    return { id };
  },

  /**
   * Add a leaf element item into a group.
   *
   * @param {{ groupId, element: HTMLElement|string, id? }} options
   */
  addItem(options = {}) {
    const { groupId, element, id = `item-${Date.now()}-${Math.random().toString(36).slice(2)}` } = options;
    if (!groupId || element === undefined) throw new Error('[Sidebar.addItem] groupId and element are required');

    if (!this._treeItems.has(groupId)) this._treeItems.set(groupId, []);
    this._treeItems.get(groupId).push({ id, element });

    const group = this._treeGroups.get(groupId);
    if (group) this._rebuildTabTree(group.tabId);
    return { id };
  },

  /**
   * Set a single content panel element for a group.
   * Replaces addItem entries visually (both can coexist).
   *
   * @param {string} groupId
   * @param {HTMLElement} element
   */
  setGroupContent(groupId, element) {
    this._treeContent.set(groupId, element);
    const group = this._treeGroups.get(groupId);
    if (group) this._rebuildTabTree(group.tabId);
  },

  /**
   * Remove all leaf items from a group.
   * Useful before re-populating with fresh data.
   *
   * @param {string} groupId
   */
  clearGroupItems(groupId) {
    this._treeItems.delete(groupId);
    const group = this._treeGroups.get(groupId);
    if (group) this._rebuildTabTree(group.tabId);
  },

  // ── Internal rendering ─────────────────────────────────────────────────

  _rebuildTabTree(tabId) {
    const roots = this._treeTabRoots.get(tabId);
    if (!roots) return;

    const tree = document.createElement('div');
    tree.className = 'rd-tree';
    roots.forEach(gid => tree.appendChild(this._buildGroupEl(gid, 0)));

    // Reuse or create the content container for this tab
    const tabsPanel = this.querySelector('.tabs-panel');
    if (!tabsPanel) return;

    let contentArea = tabsPanel.querySelector('.rd-sidebar-content-area');
    if (!contentArea) {
      contentArea = document.createElement('div');
      contentArea.className = 'rd-sidebar-content-area';
      tabsPanel.appendChild(contentArea);
    }

    let container = this._contentContainers.get(tabId);
    if (!container) {
      container = document.createElement('div');
      container.className = 'rd-tab-content-container';
      container.setAttribute('data-tab', tabId);
      container.style.display = 'none';
      this._contentContainers.set(tabId, container);
      contentArea.appendChild(container);
    }

    const display = container.style.display;
    container.innerHTML = '';
    container.appendChild(tree);
    container.style.display = display;
    this._updateContentVisibility();
  },

  _buildGroupEl(groupId, depth) {
    const group = this._treeGroups.get(groupId);
    if (!group) return document.createDocumentFragment();

    const expanded = this._treeExpanded.has(groupId);

    const el = document.createElement('div');
    el.className = `rd-tree-group${expanded ? ' rd-tree-group--expanded' : ''}${group.noHeader ? ' rd-tree-group--panel' : ''}`;
    el.dataset.groupId = groupId;

    // Header — omitted for noHeader groups
    if (!group.noHeader) {
      const header = document.createElement('button');
      header.className = 'rd-tree-group__header';
      header.style.setProperty('--rd-tree-depth', depth);
      header.setAttribute('aria-expanded', String(expanded));
      header.innerHTML = `
        <i class="bx bx-chevron-right rd-tree-group__chevron"></i>
        ${group.icon ? `<i class="${group.icon} rd-tree-group__icon"></i>` : ''}
        <span class="rd-tree-group__label">${group.label}</span>
      `;
      header.addEventListener('click', () => this._toggleTreeGroup(groupId));
      el.appendChild(header);
    }

    // Body — always visible for noHeader, lazy for normal groups
    if (expanded) {
      const body = document.createElement('div');
      body.className = 'rd-tree-group__body';

      // Nested child groups (recursive, unlimited depth)
      group.children?.forEach(childId => body.appendChild(this._buildGroupEl(childId, depth + 1)));

      // Custom content panel (setGroupContent)
      if (this._treeContent.has(groupId)) body.appendChild(this._treeContent.get(groupId));

      // Leaf items (addItem)
      this._treeItems.get(groupId)?.forEach(item => {
        if (item.element instanceof HTMLElement) {
          body.appendChild(item.element);
        } else {
          const wrap = document.createElement('div');
          wrap.innerHTML = item.element;
          body.appendChild(wrap.firstElementChild ?? wrap);
        }
      });

      el.appendChild(body);
    }

    return el;
  },

  _toggleTreeGroup(groupId) {
    const group = this._treeGroups.get(groupId);
    if (!group) return;

    const wasExpanded = this._treeExpanded.has(groupId);
    wasExpanded ? this._treeExpanded.delete(groupId) : this._treeExpanded.add(groupId);
    const nowExpanded = !wasExpanded;

    const detail = {
      groupId,
      group,
      tabId: group.tabId,
      path: this._getTreeGroupPath(groupId),
    };

    // Inline callbacks
    group.onClick?.(group, detail);
    nowExpanded ? group.onExpand?.(group, detail) : group.onCollapse?.(group, detail);

    // Global events
    this.dispatchEvent(new CustomEvent(
      nowExpanded ? 'rd-group-expand' : 'rd-group-collapse',
      { bubbles: true, composed: true, detail },
    ));

    this._rebuildTabTree(group.tabId);
  },

  _getTreeGroupPath(groupId) {
    const path = [];
    let cur = this._treeGroups.get(groupId);
    while (cur) {
      path.unshift(cur.id);
      cur = cur.parentId ? this._treeGroups.get(cur.parentId) : null;
    }
    return path;
  },

});

if (!customElements.get('rd-sidebar')) customElements.define('rd-sidebar', RdSideBar);
